import { sendToUnityWithResponse } from "../../components/Unityboards/UnityBridge";
import { apiService } from "../apiService";
import { getFreshToken } from "../api/base";
import { useViewRDataStore } from "../../store/viewRDataStore";
import { useUploadStore, isEditorImportExecutionCurrent, type ExtendedUploadItem } from "../../store/uploadStore";
import { assertEditorImportExecution, assertEditorImportOriginalContext } from "./editorImportAdmission";
import { buildUnitySaveAttemptId } from "../../utils/unitySaveAttempt";
import {
  createImportedVseSelectionContext,
  waitForImportedVseSelection,
} from "../../utils/editor/importSelection";
import { getEditorSaveApiBaseUrl } from "./saveApi";
import {
  EDITOR_LIBRARY_ATTACH_PRECONVERSION_SAVE_REASON,
  clearEditorLibraryAttachSession,
  registerEditorLibraryAttachSession,
} from "./editorLibraryAttachSession";
import {
  type EditorImportVseReadiness,
  validateEditorImportVseEntry,
  waitForViewRDataAfterEditorImportReload,
} from "./editorImportPostReloadGate";
import { reloadUpdatedViewrBeforeEditorImport } from "./editorImportViewrReload";
import type { UnitySpaceNavigationStatePayload } from "../../types/unity";
import {
  getCanonicalSpaceSnapshotForUpload,
  getUploadSpaceTargetForImport,
  UNITY_SPACE_NAVIGATION_STATE_EVENT,
} from "./spaceNavigationCanonicalState";
import {
  getSpaceAddPersistBarrierSnapshot,
  isSpaceAddPersistBarrierBlockingForTarget,
  isSpaceAddPersistSaveAlive,
  waitForSpaceAddPersistBarrier,
} from "./spaceAddPersistBarrier";
import { isSpaceSwitchActive } from "./spaceSwitchState";
import {
  type UnityObjectImportStatePayload,
  UNITY_OBJECT_IMPORT_STATE_EVENT,
  OBJECT_IMPORT_LOAD_CANCELLED_REASON,
} from "./objectImportState";
import { waitForUnitySaveJsonReady } from "./editorImportHybridConversion";
import {
  prepareLibraryAttachViewrUpdateSse,
  waitForLibraryAttachVseComplete,
} from "./editorLibraryAttachVseCompletion";
import { isUnityRuntimeReadyAtLeast } from "./unityRuntimeBridgeState";
import {
  captureEditorProjectPackageBaseline,
  markEditorProjectClean,
} from "./editorProjectDirty";
import {
  EditorLibraryAttachDeferredError,
  isEditorLibraryAttachDeferredError,
  isLibraryAttachResumePending,
  readLibraryAttachResumePayload,
  type LibraryAttachDeferredPopup,
  type LibraryAttachResumePayload,
  type LibraryAttachResumePhase,
} from "./editorLibraryAttachResume";

export const LIBRARY_ATTACH_IMPORT_FINISHED_EVENT =
  "viewr:libraryAttachImportFinished";

export type RunEditorLibraryAttachImportArgs = {
  userToken: string;
  libraryObjectId: string;
  libraryVseGcsPath: string;
  libraryVsePublicUrl: string;
  displayName: string;
  viewrGcsPath: string;
  targetSpaceIndex: number;
  uploadItemId: string;
  executionId?: string;
};

export type RunEditorLibraryAttachImportResult = {
  jobId: string;
  expectedVseIndex: number;
};

export type LibraryAttachImportFinishedDetail = {
  uploadItemId: string;
  status: "success" | "failed";
  message?: string;
  fileName?: string;
};

/** Upload·resume·restore가 동시에 같은 attach를 시작할 때 단일 workflow Promise로 합류 */
const libraryAttachWorkflowPromises = new Map<
  string,
  Promise<RunEditorLibraryAttachImportResult | null>
>();

/** Runtime dedupe ends on route unmount; the upload item and server Job survive. */
export function releaseEditorLibraryAttachRuntime(projectPath: string): void {
  for (const id of Array.from(libraryAttachWorkflowPromises.keys())) {
    const item = useUploadStore.getState().items[id];
    if (item?.originViewrGcsPath !== projectPath || !item.editorImportAdmission) continue;
    libraryAttachWorkflowPromises.delete(id);
    useUploadStore.getState().beginEditorImportExecution(id);
    useUploadStore.getState().upsertItem({ id, editorImportRuntimeState: "awaiting_user_restore" });
  }
}

export function isEditorLibraryAttachWorkflowInFlight(
  uploadItemId: string,
): boolean {
  return libraryAttachWorkflowPromises.has(uploadItemId);
}

function assertEditorUnityReadyForLibraryAttach(): void {
  if (!isUnityRuntimeReadyAtLeast("handlers_ready")) {
    throw new EditorLibraryAttachDeferredError();
  }
}

function normalizeLibraryAttachProjectPath(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function isLibraryAttachRuntimeContextDeferred(args: {
  viewrGcsPath: string;
  targetSpaceIndex: number;
}): boolean {
  if (
    typeof window === "undefined" ||
    !window.location.pathname.startsWith("/editor") ||
    !isUnityRuntimeReadyAtLeast("handlers_ready")
  ) {
    return true;
  }

  const viewRData = useViewRDataStore.getState().viewRData;
  const currentProjectPath = normalizeLibraryAttachProjectPath(
    viewRData?.viewrGCSPath,
  );
  const expectedProjectPath = normalizeLibraryAttachProjectPath(
    args.viewrGcsPath,
  );
  if (
    currentProjectPath &&
    expectedProjectPath &&
    currentProjectPath !== expectedProjectPath
  ) {
    return true;
  }

  const canonical = getCanonicalSpaceSnapshotForUpload();
  return canonical.currentSpaceIndex !== args.targetSpaceIndex;
}

function assertLibraryAttachRuntimeTargetContext(args: {
  uploadItemId: string;
  viewrGcsPath: string;
  targetSpaceIndex: number;
}): void {
  if (isLibraryAttachRuntimeContextDeferred(args)) {
    throw new EditorLibraryAttachDeferredError();
  }

  const viewRData = useViewRDataStore.getState().viewRData;
  const spaces = Array.isArray(viewRData?.spaces) ? viewRData.spaces : [];
  if (!spaces[args.targetSpaceIndex]) {
    throw new Error("library_attach_target_space_missing");
  }

  const targetSpaceName =
    useUploadStore.getState().items[args.uploadItemId]?.targetSpaceName ?? null;
  if (
    targetSpaceName &&
    spaces[args.targetSpaceIndex]?.name !== targetSpaceName
  ) {
    throw new Error("library_attach_target_space_changed");
  }
}

function mergeLibraryAttachResumePayload(
  uploadItemId: string,
  patch: Partial<LibraryAttachResumePayload>,
): LibraryAttachResumePayload | null {
  const item = useUploadStore.getState().items[uploadItemId];
  const existing = readLibraryAttachResumePayload(item);
  if (!existing && !patch.libraryObjectId) {
    return null;
  }
  const next: LibraryAttachResumePayload = {
    libraryObjectId:
      patch.libraryObjectId ?? existing?.libraryObjectId ?? "",
    libraryVseGcsPath:
      patch.libraryVseGcsPath ?? existing?.libraryVseGcsPath ?? "",
    libraryVsePublicUrl:
      patch.libraryVsePublicUrl ?? existing?.libraryVsePublicUrl ?? "",
    displayName: patch.displayName ?? existing?.displayName ?? "Untitled",
    viewrGcsPath: patch.viewrGcsPath ?? existing?.viewrGcsPath ?? "",
    targetSpaceIndex:
      patch.targetSpaceIndex ?? existing?.targetSpaceIndex ?? 0,
    phase: patch.phase ?? existing?.phase ?? "started",
    jobId: patch.jobId !== undefined ? patch.jobId : (existing?.jobId ?? null),
    expectedVseIndex:
      patch.expectedVseIndex !== undefined
        ? patch.expectedVseIndex
        : (existing?.expectedVseIndex ?? null),
    deferredPopup:
      patch.deferredPopup !== undefined
        ? patch.deferredPopup
        : (existing?.deferredPopup ?? null),
  };

  useUploadStore.getState().upsertItem?.({
    id: uploadItemId,
    fileData: {
      ...(item?.fileData ?? {}),
      libraryAttachResume: next,
    },
  });
  return next;
}

function setLibraryAttachResumePhase(
  uploadItemId: string,
  phase: LibraryAttachResumePhase,
  patch: Partial<LibraryAttachResumePayload> = {},
): void {
  mergeLibraryAttachResumePayload(uploadItemId, { ...patch, phase });
}

function registerSessionFromResumePayload(
  uploadItemId: string,
  payload: LibraryAttachResumePayload,
): void {
  if (!payload.jobId || payload.expectedVseIndex == null) {
    return;
  }
  registerEditorLibraryAttachSession({
    uploadItemId,
    jobId: payload.jobId,
    libraryObjectId: payload.libraryObjectId,
    libraryVseGcsPath: payload.libraryVseGcsPath,
    libraryVsePublicUrl: payload.libraryVsePublicUrl,
    displayName: payload.displayName,
    viewrGcsPath: payload.viewrGcsPath,
    targetSpaceIndex: payload.targetSpaceIndex,
    expectedVseIndex: payload.expectedVseIndex,
  });
}

export function dispatchLibraryAttachImportFinished(
  detail: LibraryAttachImportFinishedDetail,
): void {
  window.dispatchEvent(
    new CustomEvent(LIBRARY_ATTACH_IMPORT_FINISHED_EVENT, { detail }),
  );
}

export function queueLibraryAttachDeferredPopup(
  uploadItemId: string,
  popup: LibraryAttachDeferredPopup,
): void {
  mergeLibraryAttachResumePayload(uploadItemId, { deferredPopup: popup });
}

export function consumeLibraryAttachDeferredPopup(
  uploadItemId: string,
): LibraryAttachDeferredPopup | null {
  const item = useUploadStore.getState().items[uploadItemId];
  const payload = readLibraryAttachResumePayload(item);
  const popup = payload?.deferredPopup ?? null;
  if (!popup) {
    return null;
  }
  mergeLibraryAttachResumePayload(uploadItemId, { deferredPopup: null });
  return popup;
}

function listPendingLibraryAttachUploadItems(
  viewrGcsPath: string,
): ExtendedUploadItem[] {
  const items = Object.values(useUploadStore.getState().items);
  return items.filter((item) => {
    if (item.flow !== "editor_library_attach" || item.cancelled) {
      return false;
    }
    if (item.originViewrGcsPath !== viewrGcsPath) {
      return false;
    }
    const resume = readLibraryAttachResumePayload(item);
    if (!resume || !isLibraryAttachResumePending(resume)) {
      return false;
    }
    if (item.status === "failed" || item.status === "cancelled") {
      return false;
    }
    return true;
  });
}

function reportLibraryAttachConvertingProgress(
  uploadItemId: string,
  progress: number,
): void {
  useUploadStore.getState().setConverting?.({
    id: uploadItemId,
    progress,
  });
}

function reportLibraryAttachRenderingProgress(
  uploadItemId: string,
  progress: number,
): void {
  useUploadStore.getState().setRendering?.({
    id: uploadItemId,
    progress,
  });
}

function isRecoverableLibraryAttachImportGateError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : "";
  return (
    message === "editor_import_viewr_data_sync_timeout" ||
    message === "editor_import_vse_file_info_missing" ||
    message === "editor_import_vse_entry_missing" ||
    message === "editor_import_viewr_data_missing" ||
    message === "editor_import_space_missing"
  );
}

export const LIBRARY_ATTACH_VSE_ENTRY_NOT_READY_ERROR =
  "library_attach_vse_entry_not_ready";

export const LIBRARY_ATTACH_VSE_LOAD_COMPLETE_TIMEOUT_ERROR =
  "library_attach_vse_load_complete_timeout";

export const LIBRARY_ATTACH_VSE_LOAD_FAILED_ERROR =
  "library_attach_vse_load_failed";

export const LIBRARY_ATTACH_SPACE_NOT_READY_ERROR =
  "library_attach_space_not_ready";
export const LIBRARY_ATTACH_SPACE_PERSIST_TIMEOUT_ERROR =
  "library_attach_space_persist_timeout";
export const LIBRARY_ATTACH_SPACE_TARGET_CHANGED_ERROR =
  "library_attach_space_target_changed";

function waitForLibraryAttachPreflightTick(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 100));
}

/**
 * canonical이 안정 상태인데 현재 Space가 target과 다르면, 그 target은 더 이상
 * 도달할 수 없다. (사용자가 대기 중 다른 Space로 전환했거나, 확정 전에 캡처한
 * index가 Unity 확정값과 달랐던 경우) timeout까지 붙잡아 두지 않고 즉시 실패시킨다.
 */
function isLibraryAttachSpaceTargetUnreachable(args: {
  canonical: ReturnType<typeof getCanonicalSpaceSnapshotForUpload>;
  targetSpaceIndex: number;
}): boolean {
  return (
    args.canonical.isStable &&
    typeof args.canonical.currentSpaceIndex === "number" &&
    Number.isFinite(args.canonical.currentSpaceIndex) &&
    args.canonical.currentSpaceIndex !== args.targetSpaceIndex
  );
}

async function waitForLibraryAttachSpaceReady(args: {
  targetSpaceIndex: number;
  viewrGcsPath: string;
  assertCurrent?: () => void;
  timeoutMs?: number;
}): Promise<number> {
  const timeoutMs = args.timeoutMs ?? 30_000;
  const barrier = getSpaceAddPersistBarrierSnapshot();

  // Add Space persist가 실패한 Space에는 attach를 제출하지 않는다. 서버 .viewr에
  // 그 Space가 없을 수 있어 attach가 성공해도 결과가 유실된다.
  // isSpaceAddPersistBarrierBlocking은 사용자가 재시도할 수 있도록 failed를
  // 차단으로 보지 않으므로, 여기서 별도로 판정한다.
  if (
    barrier.status === "failed" &&
    barrier.targetSpaceIndex === args.targetSpaceIndex
  ) {
    console.error("[EDITOR library-attach] space add persist failed", {
      targetSpaceIndex: args.targetSpaceIndex,
      error: barrier.error,
      persistBarrier: barrier,
    });
    throw new Error(barrier.error ?? "space_add_persist_failed");
  }

  if (
    isSpaceAddPersistBarrierBlockingForTarget(
      args.targetSpaceIndex,
      barrier,
    )
  ) {
    // 이 대기는 최대 수십 초까지 갈 수 있고 그동안 다른 로그가 전혀 나오지 않는다.
    // 진입·종료를 남기지 않으면 콘솔만으로는 멈춘 지점을 알 수 없다.
    console.log("[EDITOR library-attach] waiting for space add persist", {
      targetSpaceIndex: args.targetSpaceIndex,
      persistBarrier: barrier,
      saveAlive: isSpaceAddPersistSaveAlive(barrier),
    });
    try {
      const resolved = await waitForSpaceAddPersistBarrier({
        targetSpaceIndex: args.targetSpaceIndex,
      });
      console.log("[EDITOR library-attach] space add persist wait released", {
        targetSpaceIndex: args.targetSpaceIndex,
        status: resolved.status,
        // persisted가 아니면 저장 완료를 확인하지 못한 채 차단만 해제된 것이다.
        released: resolved.status === "persisted" ? "persisted" : "stale_unblocked",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("[EDITOR library-attach] space add persist wait failed", {
        targetSpaceIndex: args.targetSpaceIndex,
        error: message,
        persistBarrier: getSpaceAddPersistBarrierSnapshot(),
      });
      if (message === "space_add_persist_timeout") {
        throw new Error(LIBRARY_ATTACH_SPACE_PERSIST_TIMEOUT_ERROR);
      }
      throw error;
    }
  }

  args.assertCurrent?.();
  assertEditorUnityReadyForLibraryAttach();
  try {
    await sendToUnityWithResponse("reactToUnity_requestViewRData", {
      reason: "library_attach_space_preflight",
      targetSpaceIndex: args.targetSpaceIndex,
      viewrGcsPath: args.viewrGcsPath,
    }, { assertCurrent: args.assertCurrent });
  } catch (error) {
    console.warn(
      "[EDITOR library-attach] space preflight requestViewRData failed; polling current snapshot",
      error,
    );
  }

  const deadline = Date.now() + timeoutMs;
  let loggedInitialState = false;
  while (Date.now() <= deadline) {
    args.assertCurrent?.();
    const canonical = getCanonicalSpaceSnapshotForUpload();
    const target = getUploadSpaceTargetForImport();
    const viewRData = useViewRDataStore.getState().viewRData;
    const spaces = Array.isArray(viewRData?.spaces) ? viewRData.spaces : [];
    const vdbCurrentSpaceIndex =
      typeof viewRData?.currentSelectVSIndex === "number"
        ? viewRData.currentSelectVSIndex
        : null;
    const canonicalMatches =
      canonical.isStable &&
      canonical.currentSpaceIndex === args.targetSpaceIndex &&
      (canonical.targetSpaceIndex === null ||
        canonical.targetSpaceIndex === args.targetSpaceIndex);
    const vdbContainsTarget = Boolean(spaces[args.targetSpaceIndex]);

    if (!loggedInitialState) {
      loggedInitialState = true;
      console.log("[EDITOR library-attach] space preflight", {
        targetSpaceIndex: args.targetSpaceIndex,
        canonicalCurrentSpaceIndex: canonical.currentSpaceIndex,
        canonicalTargetSpaceIndex: canonical.targetSpaceIndex,
        canonicalPhase: canonical.phase,
        canonicalTransactionId: canonical.transactionId,
        vdbSpacesLength: spaces.length,
        vdbCurrentSpaceIndex,
        uploadTargetReadiness: target.readiness,
        persistBarrier: getSpaceAddPersistBarrierSnapshot(),
      });
    }

    // A stable React store fallback is retained for existing projects that have
    // not emitted a canonical navigation payload yet. Active Add Space flows
    // always require Unity canonical confirmation above.
    const fallbackMatches =
      !isSpaceSwitchActive() &&
      target.readiness === "ready" &&
      target.currentSpaceIndex === args.targetSpaceIndex;

    if (
      !canonicalMatches &&
      isLibraryAttachSpaceTargetUnreachable({
        canonical,
        targetSpaceIndex: args.targetSpaceIndex,
      })
    ) {
      console.error("[EDITOR library-attach] space preflight target changed", {
        targetSpaceIndex: args.targetSpaceIndex,
        canonicalCurrentSpaceIndex: canonical.currentSpaceIndex,
        canonicalTransactionId: canonical.transactionId,
        canonicalPhase: canonical.phase,
      });
      if (args.assertCurrent) throw new EditorLibraryAttachDeferredError();
      throw new Error(LIBRARY_ATTACH_SPACE_TARGET_CHANGED_ERROR);
    }

    if ((canonicalMatches || fallbackMatches) && vdbContainsTarget) {
      console.log("[EDITOR library-attach] space preflight ready", {
        targetSpaceIndex: args.targetSpaceIndex,
        currentSpaceIndex:
          canonical.currentSpaceIndex ?? vdbCurrentSpaceIndex,
        canonicalTransactionId: canonical.transactionId,
        vdbSpacesLength: spaces.length,
        vdbCurrentSpaceIndex,
      });
      return args.targetSpaceIndex;
    }

    await waitForLibraryAttachPreflightTick();
  }

  const finalCanonical = getCanonicalSpaceSnapshotForUpload();
  const finalViewRData = useViewRDataStore.getState().viewRData;
  console.error("[EDITOR library-attach] space preflight failed", {
    targetSpaceIndex: args.targetSpaceIndex,
    canonicalCurrentSpaceIndex: finalCanonical.currentSpaceIndex,
    canonicalTargetSpaceIndex: finalCanonical.targetSpaceIndex,
    canonicalPhase: finalCanonical.phase,
    canonicalTransactionId: finalCanonical.transactionId,
    vdbSpacesLength: finalViewRData?.spaces?.length ?? 0,
    vdbCurrentSpaceIndex: finalViewRData?.currentSelectVSIndex ?? null,
    persistBarrier: getSpaceAddPersistBarrierSnapshot(),
  });
  throw new Error(LIBRARY_ATTACH_SPACE_NOT_READY_ERROR);
}

async function prepareLibraryAttachMeshImportReadiness(args: {
  uploadItemId: string;
  viewrGcsPath: string;
  targetSpaceIndex: number;
  jobId: string;
  expectedVseIndex: number;
}): Promise<EditorImportVseReadiness> {
  const hintViewRData = useViewRDataStore.getState().viewRData;

  try {
    return validateEditorImportVseEntry(
      hintViewRData,
      args.targetSpaceIndex,
      args.expectedVseIndex,
    );
  } catch {
    // attach-vse 직후 store가 아직 동기화되지 않은 경우 requestViewRData로만 대기
  }

  try {
    await sendToUnityWithResponse("reactToUnity_requestViewRData", {
      reason: "library_attach_mesh_import",
      targetSpaceIndex: args.targetSpaceIndex,
      expectedVseIndex: args.expectedVseIndex,
      jobId: args.jobId,
      viewrGcsPath: args.viewrGcsPath,
    });
  } catch (error) {
    console.warn(
      "[EDITOR library-attach] requestViewRData before mesh import failed; waiting on store sync",
      error,
    );
  }

  try {
    const freshViewRData = await waitForViewRDataAfterEditorImportReload({
      targetSpaceIndex: args.targetSpaceIndex,
      expectedVseIndex: args.expectedVseIndex,
    });
    return validateEditorImportVseEntry(
      freshViewRData,
      args.targetSpaceIndex,
      args.expectedVseIndex,
    );
  } catch (error) {
    if (!isRecoverableLibraryAttachImportGateError(error)) {
      throw error;
    }
    console.error(
      "[EDITOR library-attach] VSE slot not ready after attach-vse; refusing mesh import with guessed index",
      {
        uploadItemId: args.uploadItemId,
        targetSpaceIndex: args.targetSpaceIndex,
        expectedVseIndex: args.expectedVseIndex,
        error,
      },
    );
    throw new Error(LIBRARY_ATTACH_VSE_ENTRY_NOT_READY_ERROR);
  }
}

function readUnityModelLoadCompleteStatus(detail: unknown): string | null {
  if (detail && typeof detail === "object" && "status" in detail) {
    const nextStatus = (detail as { status?: unknown }).status;
    return typeof nextStatus === "string" ? nextStatus : null;
  }
  if (typeof detail === "string") {
    try {
      const parsed = JSON.parse(detail) as { status?: unknown };
      return typeof parsed?.status === "string" ? parsed.status : null;
    } catch {
      return null;
    }
  }
  return null;
}

function readModelLoadCompleteIndices(detail: unknown): {
  spaceIndex: number | null;
  vseIndex: number | null;
} {
  const readFromRecord = (record: Record<string, unknown>) => ({
    spaceIndex:
      typeof record.spaceIndex === "number" ? record.spaceIndex : null,
    vseIndex: typeof record.vseIndex === "number" ? record.vseIndex : null,
  });

  if (detail && typeof detail === "object") {
    return readFromRecord(detail as Record<string, unknown>);
  }
  if (typeof detail === "string") {
    try {
      const parsed = JSON.parse(detail) as Record<string, unknown>;
      return readFromRecord(parsed);
    } catch {
      return { spaceIndex: null, vseIndex: null };
    }
  }
  return { spaceIndex: null, vseIndex: null };
}

function isLibraryAttachMeshNavigationPhase(
  phase: UnitySpaceNavigationStatePayload["phase"] | null | undefined,
): boolean {
  return phase === "vse_model_ready" || phase === "completed";
}

function matchesLibraryAttachMeshNavigationTarget(
  detail: UnitySpaceNavigationStatePayload | undefined,
  targetSpaceIndex: number,
  expectedVseIndex: number,
): boolean {
  if (!detail || detail.status === "failed" || detail.phase === "failed") {
    return false;
  }
  if (!isLibraryAttachMeshNavigationPhase(detail.phase)) {
    return false;
  }
  const resolvedSpaceIndex =
    typeof detail.currentSpaceIndex === "number"
      ? detail.currentSpaceIndex
      : detail.targetSpaceIndex;
  if (
    typeof resolvedSpaceIndex !== "number" ||
    resolvedSpaceIndex !== targetSpaceIndex
  ) {
    return false;
  }
  if (typeof detail.loadableVseCountInCurrentSpace === "number") {
    return detail.loadableVseCountInCurrentSpace > expectedVseIndex;
  }
  return detail.hasLoadableVseInCurrentSpace !== false;
}

function matchesLibraryAttachMeshModelLoadComplete(
  detail: unknown,
  targetSpaceIndex: number,
  expectedVseIndex: number,
): boolean {
  const status = readUnityModelLoadCompleteStatus(detail);
  if (status && status !== "vse_load_complete") {
    return false;
  }
  const { spaceIndex, vseIndex } = readModelLoadCompleteIndices(detail);
  if (
    status === "vse_load_complete" &&
    spaceIndex === null &&
    vseIndex === null
  ) {
    return true;
  }
  if (spaceIndex !== targetSpaceIndex) {
    return false;
  }
  if (vseIndex !== null) {
    return vseIndex === expectedVseIndex;
  }
  return false;
}

function waitForEditorImportVseLoadComplete(args: {
  uploadItemId: string;
  jobId?: string | null;
  targetSpaceIndex: number;
  expectedVseIndex: number;
  timeoutMs?: number;
  abortSignal?: AbortSignal;
}): Promise<void> {
  const { uploadItemId, jobId, targetSpaceIndex, expectedVseIndex } = args;
  const timeoutMs = args.timeoutMs ?? 45_000;

  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      args.abortSignal?.removeEventListener("abort", handleAbort);
      window.removeEventListener(
        "unity:modelLoadComplete",
        handleModelLoadComplete as EventListener,
      );
      window.removeEventListener(
        UNITY_SPACE_NAVIGATION_STATE_EVENT,
        handleSpaceNavigationState as EventListener,
      );
      window.removeEventListener(
        UNITY_OBJECT_IMPORT_STATE_EVENT,
        handleObjectImportState as EventListener,
      );
    };

    const finishSuccess = (
      source:
        | "object_import_ready"
        | "vse_load_complete"
        | "vse_model_ready"
        | "model_load_complete",
    ) => {
      console.log("[EDITOR library-attach] mesh load complete", {
        uploadItemId,
        jobId,
        source,
        targetSpaceIndex,
        expectedVseIndex,
      });
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve();
    };

    const finishFailure = (error: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(error);
    };

    const handleAbort = () => {
      finishFailure(new Error("library_attach_vse_load_complete_cancelled"));
    };

    const timeoutId = window.setTimeout(() => {
      console.error(
        "[EDITOR library-attach] vse_load_complete not received before timeout",
        { uploadItemId, timeoutMs, targetSpaceIndex, expectedVseIndex },
      );
      finishFailure(new Error(LIBRARY_ATTACH_VSE_LOAD_COMPLETE_TIMEOUT_ERROR));
    }, timeoutMs);

    const handleModelLoadComplete = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      const status = readUnityModelLoadCompleteStatus(detail);
      if (status === "vse_load_failed") {
        finishFailure(new Error(LIBRARY_ATTACH_VSE_LOAD_FAILED_ERROR));
        return;
      }
      if (
        !matchesLibraryAttachMeshModelLoadComplete(
          detail,
          targetSpaceIndex,
          expectedVseIndex,
        )
      ) {
        return;
      }
      finishSuccess(
        status === "vse_load_complete" ? "vse_load_complete" : "model_load_complete",
      );
    };

    const handleSpaceNavigationState = (event: Event) => {
      const detail = (
        event as CustomEvent<UnitySpaceNavigationStatePayload | undefined>
      ).detail;
      if (
        typeof detail?.currentSpaceIndex === "number" &&
        detail.currentSpaceIndex !== targetSpaceIndex
      ) {
        finishFailure(new EditorLibraryAttachDeferredError());
        return;
      }
      if (
        !matchesLibraryAttachMeshNavigationTarget(
          detail,
          targetSpaceIndex,
          expectedVseIndex,
        )
      ) {
        return;
      }
      finishSuccess("vse_model_ready");
    };

    const handleObjectImportState = (event: Event) => {
      const detail = (
        event as CustomEvent<UnityObjectImportStatePayload | undefined>
      ).detail;
      if (!detail) {
        return;
      }
      if (jobId && detail.jobId !== jobId) {
        return;
      }
      if (detail.targetSpaceIndex !== targetSpaceIndex) {
        return;
      }
      if (detail.vseIndex !== expectedVseIndex) {
        return;
      }
      if (detail.phase === "failed" || detail.status === "failed") {
        // STUDIO-ASSET-003: Space 전환이 in-flight 로드를 취소한 것은 실패가 아니라 restore 대기다.
        if (detail.reasonCode === OBJECT_IMPORT_LOAD_CANCELLED_REASON) {
          finishFailure(new EditorLibraryAttachDeferredError());
          return;
        }
        finishFailure(new Error(LIBRARY_ATTACH_VSE_LOAD_FAILED_ERROR));
        return;
      }
      if (detail.phase === "ready" && detail.status === "success") {
        finishSuccess("object_import_ready");
      }
    };

    window.addEventListener(
      "unity:modelLoadComplete",
      handleModelLoadComplete as EventListener,
    );
    window.addEventListener(
      UNITY_SPACE_NAVIGATION_STATE_EVENT,
      handleSpaceNavigationState as EventListener,
    );
    window.addEventListener(
      UNITY_OBJECT_IMPORT_STATE_EVENT,
      handleObjectImportState as EventListener,
    );
    args.abortSignal?.addEventListener("abort", handleAbort, { once: true });
    if (args.abortSignal?.aborted) {
      handleAbort();
    }
  });
}

async function runLibraryAttachMeshImport(args: {
  uploadItemId: string;
  jobId: string;
  viewrGcsPath: string;
  targetSpaceIndex: number;
  expectedVseIndex: number;
  executionId?: string;
}): Promise<void> {
  const guard = () => { if (args.executionId) assertEditorImportOriginalContext(args.uploadItemId, args.executionId); };
  guard();
  assertLibraryAttachRuntimeTargetContext(args);
  reportLibraryAttachConvertingProgress(args.uploadItemId, 78);

  const vseLoadCompleteAbortController = new AbortController();
  const vseLoadCompletePromise = waitForEditorImportVseLoadComplete({
    uploadItemId: args.uploadItemId,
    jobId: args.jobId,
    targetSpaceIndex: args.targetSpaceIndex,
    expectedVseIndex: args.expectedVseIndex,
    abortSignal: vseLoadCompleteAbortController.signal,
  });
  vseLoadCompletePromise.catch(() => undefined);

  let readiness: EditorImportVseReadiness;
  try {
    await reloadUpdatedViewrBeforeEditorImport({
      uploadItemId: args.uploadItemId,
      viewrGcsPath: args.viewrGcsPath,
      targetSpaceIndex: args.targetSpaceIndex,
      jobId: args.jobId,
    });

    guard();
    reportLibraryAttachConvertingProgress(args.uploadItemId, 82);

    reportLibraryAttachConvertingProgress(args.uploadItemId, 84);

    readiness = await prepareLibraryAttachMeshImportReadiness({
      uploadItemId: args.uploadItemId,
      viewrGcsPath: args.viewrGcsPath,
      targetSpaceIndex: args.targetSpaceIndex,
      jobId: args.jobId,
      expectedVseIndex: args.expectedVseIndex,
    });
    guard();
    reportLibraryAttachConvertingProgress(args.uploadItemId, 88);

    console.log("[EDITOR library-attach] waiting for .viewr reload mesh", {
      vseIndex: readiness.vseIndex,
    });

    reportLibraryAttachRenderingProgress(args.uploadItemId, 90);
    await vseLoadCompletePromise;
    guard();
    reportLibraryAttachRenderingProgress(args.uploadItemId, 96);
  } finally {
    vseLoadCompleteAbortController.abort();
  }

  assertLibraryAttachRuntimeTargetContext(args);

  const selectionContext = createImportedVseSelectionContext({
    viewRData: readiness.viewRData,
    targetSpaceIndex: args.targetSpaceIndex,
    uploadItemId: args.uploadItemId,
    baselineVseCount: args.expectedVseIndex,
  });

  const importedSelection = await waitForImportedVseSelection({
    initialViewRData: readiness.viewRData,
    selectionContext,
    sendToUnity: (command, payload) => { guard(); return sendToUnityWithResponse(command, payload, { assertCurrent: guard }); },
    allowTargetSpaceSwitch: false,
    requireSelectionConfirmation: Boolean(args.executionId),
    assertCurrent: args.executionId ? guard : undefined,
    log: (message, data) => {
      console.log(`[EDITOR library-attach] ${message}`, data);
    },
  });
  guard();
  if (!importedSelection) {
    throw new Error("library_attach_selection_unconfirmed");
  }
  reportLibraryAttachRenderingProgress(args.uploadItemId, 99);
}

/**
 * Library import 정본: VDB → attach-vse → .viewr reload → mesh (editor_object_import 후반과 동일).
 * Packager 완료 전에는 modelLoadRequest를 보내지 않는다.
 */
export async function runEditorLibraryAttachImport({
  userToken,
  libraryObjectId,
  libraryVseGcsPath,
  libraryVsePublicUrl,
  displayName,
  viewrGcsPath,
  targetSpaceIndex,
  uploadItemId,
  executionId,
}: RunEditorLibraryAttachImportArgs): Promise<RunEditorLibraryAttachImportResult> {
  const guard = () => { if (executionId) assertEditorImportExecution(uploadItemId, executionId); };
  const guardContext = () => { guard(); if (executionId) assertEditorImportOriginalContext(uploadItemId, executionId); };
  guard();
  // preflight는 Add Space persist 완료까지 대기할 수 있고, upload store는
  // localStorage에 persist된다. 대기 중 새로고침해도 재개할 수 있도록 재개 정보를
  // 대기 전에 먼저 기록한다. (expectedVseIndex는 preflight 통과 후에만 확정한다)
  mergeLibraryAttachResumePayload(uploadItemId, {
    libraryObjectId,
    libraryVseGcsPath,
    libraryVsePublicUrl,
    displayName,
    viewrGcsPath,
    targetSpaceIndex,
  });

  const effectiveTargetSpaceIndex = await waitForLibraryAttachSpaceReady({
    targetSpaceIndex,
    viewrGcsPath,
    assertCurrent: executionId ? guard : undefined,
  });
  guard();
  const existing = readLibraryAttachResumePayload(
    useUploadStore.getState().items[uploadItemId],
  );
  const startPhase: LibraryAttachResumePhase = existing?.phase ?? "started";
  if (startPhase !== "attach_complete" && startPhase !== "mesh_imported") {
    captureEditorProjectPackageBaseline({
      persistenceIdentity: uploadItemId,
      spaceIndex: effectiveTargetSpaceIndex,
    });
  }

  const viewRData = useViewRDataStore.getState().viewRData;
  const space = viewRData?.spaces?.[effectiveTargetSpaceIndex];
  const expectedVseIndex =
    existing?.expectedVseIndex ??
    (Array.isArray(space?.vseList) ? space.vseList.length : 0);

  mergeLibraryAttachResumePayload(uploadItemId, {
    libraryObjectId,
    libraryVseGcsPath,
    libraryVsePublicUrl,
    displayName,
    viewrGcsPath,
        targetSpaceIndex: effectiveTargetSpaceIndex,
    expectedVseIndex,
    phase: startPhase,
  });

  console.log("[EDITOR library-attach] start", {
    uploadItemId,
    targetSpaceIndex: effectiveTargetSpaceIndex,
    expectedVseIndex,
    libraryVseGcsPath,
    startPhase,
  });

  let jobId = existing?.jobId?.trim() ?? "";
  if (!jobId) {
    guardContext();
    const created = await apiService.createLibraryAttachJob(userToken);
    guard();
    jobId = created.job_id;
    setLibraryAttachResumePhase(uploadItemId, "started", { jobId, expectedVseIndex });
  }

  registerEditorLibraryAttachSession({
    uploadItemId,
    jobId,
    libraryObjectId,
    libraryVseGcsPath,
    libraryVsePublicUrl,
    displayName,
    viewrGcsPath,
    targetSpaceIndex: effectiveTargetSpaceIndex,
    expectedVseIndex,
  });

  useUploadStore.getState().upsertItem?.({
    id: uploadItemId,
    jobId,
    editorImportVseIndex: expectedVseIndex,
  });

  // Unity requests the VDB signed URL directly, so it does not receive apiCall's
  // 401 refresh retry. Do not pass the AuthContext token snapshot, which may have
  // expired while the Editor remained open.
  const freshToken = await getFreshToken();
  guard();
  const token = freshToken || userToken;
  const phaseRank: Record<LibraryAttachResumePhase, number> = {
    started: 0,
    vdb_saved: 1,
    attach_submitted: 2,
    attach_complete: 3,
    mesh_imported: 4,
    failed: -1,
  };

  if (phaseRank[startPhase] < phaseRank.vdb_saved) {
    reportLibraryAttachConvertingProgress(uploadItemId, 35);
    assertEditorUnityReadyForLibraryAttach();
    guardContext();
    const saveAttemptId = buildUnitySaveAttemptId(
      EDITOR_LIBRARY_ATTACH_PRECONVERSION_SAVE_REASON,
    );
    const readyPromise = waitForUnitySaveJsonReady({
      saveAttemptId,
      timeoutMs: 45_000,
    });
    readyPromise.catch(() => undefined);

    console.log("[EDITOR library-attach] VDB preconversion saveJson");
    await sendToUnityWithResponse(
      "reactToUnity_saveJson",
      {
        reason: EDITOR_LIBRARY_ATTACH_PRECONVERSION_SAVE_REASON,
        saveAttemptId,
        jobId,
        viewrGCSPath: viewrGcsPath,
    targetSpaceIndex: effectiveTargetSpaceIndex,
        apiBaseUrl: getEditorSaveApiBaseUrl(),
        bearerToken: token,
      },
      {
        dedupeKey: `reactToUnity_saveJson::${saveAttemptId}`,
        requiresReadyState: "handlers_ready",
        assertCurrent: guardContext,
      },
    );

    await readyPromise;
    guard();
    setLibraryAttachResumePhase(uploadItemId, "vdb_saved", { jobId });
    console.log("[EDITOR library-attach] VDB ready, submitting attach-vse");
  }

  if (phaseRank[startPhase] < phaseRank.attach_submitted) {
    reportLibraryAttachConvertingProgress(uploadItemId, 50);
    await prepareLibraryAttachViewrUpdateSse(jobId);
    guard();
    // Persist before POST: a timeout cannot permit a second attach submission.
    setLibraryAttachResumePhase(uploadItemId, "attach_submitted", { jobId });
    if (executionId) useUploadStore.getState().updateEditorImportAdmission(uploadItemId, executionId, { remotePhase: "submitting", state: "reconciling" });
    await apiService.attachLibraryVse(
      {
        job_id: jobId,
        library_object_id: libraryObjectId,
        library_vse_gcs_path: libraryVseGcsPath,
        viewr_gcs_path: viewrGcsPath,
        space_index: effectiveTargetSpaceIndex,
        vse_index: expectedVseIndex,
        display_name: displayName,
      },
      token,
    );
    guard();
    if (executionId) useUploadStore.getState().updateEditorImportAdmission(uploadItemId, executionId, { remotePhase: "accepted", state: "held" });
  }

  if (phaseRank[startPhase] < phaseRank.attach_complete) {
    reportLibraryAttachConvertingProgress(uploadItemId, 62);
    await waitForLibraryAttachVseComplete(jobId);
    guard();
    markEditorProjectClean({
      persistenceIdentity: uploadItemId,
      terminalIdentity: jobId,
      spaceIndex: effectiveTargetSpaceIndex,
      reason: "library_attach_package_persisted",
    });
    setLibraryAttachResumePhase(uploadItemId, "attach_complete", { jobId });
    reportLibraryAttachConvertingProgress(uploadItemId, 72);
    console.log("[EDITOR library-attach] attach-vse complete, reloading .viewr");
  }

  if (phaseRank[startPhase] < phaseRank.mesh_imported) {
    assertLibraryAttachRuntimeTargetContext({
      uploadItemId,
      viewrGcsPath,
      targetSpaceIndex: effectiveTargetSpaceIndex,
    });
    await runLibraryAttachMeshImport({
      uploadItemId,
      jobId,
      viewrGcsPath,
      targetSpaceIndex: effectiveTargetSpaceIndex,
      expectedVseIndex,
      executionId,
    });
    guardContext();
    setLibraryAttachResumePhase(uploadItemId, "mesh_imported", { jobId });
  }

  console.log("[EDITOR library-attach] done", { uploadItemId, jobId });

  return { jobId, expectedVseIndex };
}

export async function resumePendingEditorLibraryAttachImports(args: {
  viewrGcsPath: string;
  userToken?: string | null;
}): Promise<void> {
  if (!args.viewrGcsPath?.trim()) {
    return;
  }
  if (!isUnityRuntimeReadyAtLeast("handlers_ready")) {
    return;
  }

  const token = args.userToken ?? (await getFreshToken());
  if (!token) {
    return;
  }

  for (const item of listPendingLibraryAttachUploadItems(args.viewrGcsPath)) {
    const resume = readLibraryAttachResumePayload(item);
    if (!resume) {
      continue;
    }

    registerSessionFromResumePayload(item.id, resume);

    try {
      await executeEditorLibraryAttachImportWorkflow({
        userToken: token,
        uploadItemId: item.id,
        libraryObjectId: resume.libraryObjectId,
        libraryVseGcsPath: resume.libraryVseGcsPath,
        libraryVsePublicUrl: resume.libraryVsePublicUrl,
        displayName: resume.displayName,
        viewrGcsPath: resume.viewrGcsPath,
        targetSpaceIndex: resume.targetSpaceIndex,
      });
    } catch (error) {
      if (!isEditorLibraryAttachDeferredError(error)) {
        console.warn("[EDITOR library-attach] resume failed", {
          uploadItemId: item.id,
          error,
        });
      }
    }
  }
}

export function executeEditorLibraryAttachImportWorkflow(
  args: RunEditorLibraryAttachImportArgs,
): Promise<RunEditorLibraryAttachImportResult | null> {
  const existing = libraryAttachWorkflowPromises.get(args.uploadItemId);
  if (existing) {
    console.log(
      "[EDITOR library-attach] workflow already in flight, joining existing promise",
      { uploadItemId: args.uploadItemId },
    );
    return existing;
  }

  const item = useUploadStore.getState().items[args.uploadItemId];
  const executionId = args.executionId ?? (item?.editorImportAdmission ? useUploadStore.getState().beginEditorImportExecution(args.uploadItemId) : undefined);
  if (item?.editorImportAdmission && !executionId) return Promise.resolve(null);
  const workflowPromise = runEditorLibraryAttachImportWorkflowBody({ ...args, executionId: executionId ?? undefined });
  libraryAttachWorkflowPromises.set(args.uploadItemId, workflowPromise);
  void workflowPromise.finally(() => {
    if (libraryAttachWorkflowPromises.get(args.uploadItemId) === workflowPromise) {
      libraryAttachWorkflowPromises.delete(args.uploadItemId);
    }
  }).catch(() => undefined);
  return workflowPromise;
}

async function runEditorLibraryAttachImportWorkflowBody(
  args: RunEditorLibraryAttachImportArgs,
): Promise<RunEditorLibraryAttachImportResult | null> {
  const { uploadItemId, displayName, libraryVsePublicUrl } = args;

  try {
    const result = await runEditorLibraryAttachImport(args);
    if (args.executionId) {
      assertEditorImportOriginalContext(uploadItemId, args.executionId);
      useUploadStore.getState().releaseEditorImport({ id: uploadItemId, executionId: args.executionId, reason: "runtime_complete" });
    }

    useUploadStore.getState().upsertItem?.({
      id: uploadItemId,
      jobId: result.jobId,
    });
    useUploadStore.getState().complete?.({
      id: uploadItemId,
      fileUrl: libraryVsePublicUrl,
    });

    const successDetail: LibraryAttachImportFinishedDetail = {
      uploadItemId,
      status: "success",
      message: "Object added.",
      fileName: displayName,
    };

    if (isUnityRuntimeReadyAtLeast("handlers_ready")) {
      dispatchLibraryAttachImportFinished(successDetail);
    } else {
      queueLibraryAttachDeferredPopup(uploadItemId, {
        status: "success",
        message: successDetail.message ?? "Object added.",
        fileName: displayName,
      });
    }

    clearEditorLibraryAttachSession(uploadItemId);
    return result;
  } catch (error) {
    if (args.executionId) {
      if (!isEditorImportExecutionCurrent(uploadItemId, args.executionId)) return null;
      const admission = useUploadStore.getState().items[uploadItemId].editorImportAdmission!;
      if (admission.remotePhase !== "not_submitted") {
        useUploadStore.getState().updateEditorImportAdmission(uploadItemId, args.executionId, { state: "reconciling" });
        useUploadStore.getState().upsertItem({ id: uploadItemId, editorImportRuntimeState: "awaiting_user_restore" });
        throw new EditorLibraryAttachDeferredError();
      }
      if (!isEditorLibraryAttachDeferredError(error) && !(error instanceof Error && error.message === "editor_import_original_context_unavailable")) {
        useUploadStore.getState().releaseEditorImport({ id: uploadItemId, executionId: args.executionId, reason: "pre_submit_failure" });
      } else {
        useUploadStore.getState().upsertItem({ id: uploadItemId, editorImportRuntimeState: "awaiting_user_restore" });
        throw new EditorLibraryAttachDeferredError();
      }
    }
    const resume = readLibraryAttachResumePayload(
      useUploadStore.getState().items[uploadItemId],
    );
    const serverAccepted =
      resume?.phase === "attach_submitted" ||
      resume?.phase === "attach_complete" ||
      resume?.phase === "mesh_imported";
    if (
      !isEditorLibraryAttachDeferredError(error) &&
      serverAccepted &&
      isLibraryAttachRuntimeContextDeferred({
        viewrGcsPath: args.viewrGcsPath,
        targetSpaceIndex: args.targetSpaceIndex,
      })
    ) {
      useUploadStore.getState().setConverting?.({
        id: uploadItemId,
        progress: 60,
      });
      throw new EditorLibraryAttachDeferredError();
    }
    if (isEditorLibraryAttachDeferredError(error)) {
      console.log(
        "[EDITOR library-attach] deferred until Editor Unity is ready again.",
        { uploadItemId },
      );
      useUploadStore.getState().setConverting?.({
        id: uploadItemId,
        progress: 60,
      });
      throw error;
    }

    const message =
      error instanceof Error ? error.message : "Library import failed.";
    // 호출부의 catch는 이 함수가 null로 resolve하기 때문에 실행되지 않는다.
    // 여기서 남기지 않으면 실패가 토스트로만 보이고 콘솔에 흔적이 없다.
    console.error("[EDITOR library-attach] failed", {
      uploadItemId,
      displayName,
      error: message,
    });
    setLibraryAttachResumePhase(uploadItemId, "failed");
    useUploadStore.getState().fail?.({ id: uploadItemId, error: message });

    const failedDetail: LibraryAttachImportFinishedDetail = {
      uploadItemId,
      status: "failed",
      message: "Import failed. Try again.",
      fileName: displayName,
    };
    if (isUnityRuntimeReadyAtLeast("handlers_ready")) {
      dispatchLibraryAttachImportFinished(failedDetail);
    } else {
      queueLibraryAttachDeferredPopup(uploadItemId, {
        status: "failed",
        message: "Import failed. Try again.",
        fileName: displayName,
      });
    }

    clearEditorLibraryAttachSession(uploadItemId);
    return null;
  }
}

/** @deprecated `runEditorLibraryAttachImport` 사용 */
export const startEditorLibraryAttachImport = runEditorLibraryAttachImport;

/** @deprecated `runEditorLibraryAttachImport` 사용 */
export const completeEditorLibraryAttachAfterLoad = async (
  _uploadItemId: string,
  _userToken: string | null,
): Promise<void> => {
  throw new Error(
    "completeEditorLibraryAttachAfterLoad is deprecated; use runEditorLibraryAttachImport.",
  );
};

export const persistEditorLibraryAttachAfterLoad = completeEditorLibraryAttachAfterLoad;
